import { LANGUAGE_CATALOG, formatCount, formatPercent, tt } from '@agos/shared'

/**
 * `/settings-translations` — the translation memory, 12 859 rows of it.
 *
 * **FB-159.** The coverage panel counts three languages and the memory holds five.
 * `GET /translations/stats` answers `activeLangs: ["hu", "en", "it"]`, `uniqueSources: 4316` and
 * `expectedRows: 12948` — which is 4 316 × 3. Measured on `translation_memory`:
 *
 * | | stats | table |
 * |---|---|---|
 * | target languages | 3 | **5** — `hu` 4 346, `en` 4 216, `it` 4 196, **`cs` 99**, **`sk` 2** |
 * | distinct source texts | 4 316 | **4 375** |
 * | rows | 12 948 *expected* | **12 859** |
 *
 * So 101 rows sit in the memory, are reachable through the language filter — the catalogue offers twelve
 * languages, Czech among them — and appear in none of the figures. And *Očakávané riadky* is presented as the
 * denominator of completeness, which makes the percentage the panel implies a percentage of three languages.
 *
 * **The server reports both now** (`MIGRATION-STATUS`, 2026-08-02): `uncountedLangs` and `uncountedRows`
 * say what the per-language figures do not cover — measured live, `[{cs: 99}, {sk: 2}]` and 101 — and
 * `memorySources` gives the memory's own distinct source count beside the discovery the counts are built
 * from. So the panel can name the gap from the payload instead of describing it in prose.
 *
 * **FB-160.** The namespace filter is built from the **fetched page**:
 *
 * ```tsx
 * const namespaces = useMemo(() => {
 *   const s = new Set<string>();
 *   for (const e of entries) if (e.namespace) s.add(e.namespace);
 *   return Array.from(s).sort();
 * }, [entries]);
 * ```
 *
 * One hundred rows of 12 859, against **23** namespaces in the table. So the dropdown offers whichever
 * namespaces happen to be on screen, its options change as you page, and a namespace that is not on the
 * current page cannot be selected at all.
 *
 * **`GET /translations/namespaces` exists now** and answers all 23, so the filter is built from the
 * memory rather than from whatever is on screen.
 */

export interface TranslationEntry {
  id: number
  sourceText: string
  sourceLang: string
  targetLang: string
  translation: string
  isManualOverride: boolean
  isUntranslatable: boolean
  context: string | null
  namespace: string | null
  updatedAt: string
}

export interface EntryPage {
  entries: TranslationEntry[]
  /** The server's count for the current filter. */
  total: number
}

export type StatusFilter = 'all' | 'translated' | 'manual' | 'untranslatable'

export const PAGE_SIZES = [50, 100, 250] as const

export const DEFAULT_PAGE_SIZE = 100

/** The stats poll, and the list poll that runs only while a bulk job does. */
export const STATS_POLL_MS = 30_000
export const RUNNING_POLL_MS = 4_000

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function pageOf(payload: unknown): EntryPage {
  const row = payload as { entries?: unknown; total?: unknown; totalCount?: unknown } | undefined
  const list = Array.isArray(row?.entries) ? row.entries : []
  const entries = list.map((item) => {
    const entry = item as Record<string, unknown>
    return {
      id: typeof entry.id === 'number' ? entry.id : 0,
      sourceText: text(entry.sourceText) ?? '',
      sourceLang: text(entry.sourceLang) ?? 'sk',
      targetLang: text(entry.targetLang) ?? '',
      translation: typeof entry.translation === 'string' ? entry.translation : '',
      isManualOverride: entry.isManualOverride === true,
      isUntranslatable: entry.isUntranslatable === true,
      context: text(entry.context),
      namespace: text(entry.namespace),
      updatedAt: text(entry.updatedAt) ?? '',
    }
  })
  // The response carries both `total` and `totalCount`, the same figure twice.
  const total =
    typeof row?.totalCount === 'number'
      ? row.totalCount
      : typeof row?.total === 'number'
        ? row.total
        : entries.length
  return { entries, total }
}

// ─── FB-159: the coverage the stats describe ─────────────────────────────────

export interface LanguageStat {
  lang: string
  translated: number
  manual: number
  untranslatable: number
  missing: number
  total: number
}

export interface MemoryStats {
  uniqueSources: number
  activeLangs: string[]
  perLang: LanguageStat[]
  translated: number
  manual: number
  untranslatable: number
  missing: number
  expectedRows: number
}

export function statsOf(payload: unknown): MemoryStats | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const totals = (row.totals ?? {}) as Record<string, unknown>
  const perLang = Array.isArray(row.perLang) ? row.perLang : []
  const number = (value: unknown) => (typeof value === 'number' ? value : 0)
  return {
    uniqueSources: number(row.uniqueSources),
    activeLangs: Array.isArray(row.activeLangs)
      ? row.activeLangs.filter((entry): entry is string => typeof entry === 'string')
      : [],
    perLang: perLang.map((entry) => {
      const stat = entry as Record<string, unknown>
      return {
        lang: text(stat.lang) ?? '',
        translated: number(stat.translated),
        manual: number(stat.manual),
        untranslatable: number(stat.untranslatable),
        missing: number(stat.missing),
        total: number(stat.total),
      }
    }),
    translated: number(totals.translated),
    manual: number(totals.manual),
    untranslatable: number(totals.untranslatable),
    missing: number(totals.missing),
    expectedRows: number(totals.expectedRows),
  }
}

/**
 * What `expectedRows` is, and what it is not.
 *
 * `uniqueSources × activeLangs.length` — a **target** over the counted languages, not a count of rows. On the
 * restored data that is 4 316 × 3 = 12 948 while the memory holds 12 859 rows, 101 of them in `cs` and `sk`
 * which the stats do not count at all. So the two numbers cannot be compared to detect the gap: the target is
 * larger than the row count *and* languages are missing, at the same time.
 *
 * A first version of this file compared them and concluded the opposite. The test below caught it, and what
 * replaced it is the only honest thing available in the browser: name the languages the figures cover, say the
 * target is over those, and say the memory may hold others — which the language filter can show, since the
 * catalogue offers eleven.
 */
export function expectedRowsExplanation(stats: MemoryStats): string {
  return `${formatCount(stats.uniqueSources)} ${tt('zdrojov')} × ${formatCount(stats.activeLangs.length)} ${tt('jazykov')} - ${tt('cieľ, nie počet riadkov.')}`
}

/**
 * What the figures cover, said before the figures.
 *
 * The languages are named because *coverage* means nothing without them, and the caveat is unconditional
 * because the browser cannot tell whether a row in an uncounted language exists — the stats endpoint reports
 * only the three it considers active, and on the restored data 99 Czech rows and 2 Slovak ones are in the
 * memory and in none of the numbers.
 */
export function coverageNote(stats: MemoryStats, listTotal: number): string {
  const langs = stats.activeLangs.map((lang) => languageLabel(lang)).join(', ')
  return `${tt('Čísla nižšie pokrývajú')} ${formatCount(stats.activeLangs.length)} ${tt('jazykov')}: ${langs}. ${tt('Pamäť má')} ${formatCount(listTotal)} ${tt('riadkov a môže obsahovať aj ďalšie jazyky, ktoré server do týchto čísel nezapočítal - vidno ich vo filtri jazyka.')}`
}

/** Completeness across the active languages, and `null` when there is no denominator. */
export function completeness(stats: MemoryStats): number | null {
  if (stats.expectedRows <= 0) return null
  return ((stats.translated + stats.untranslatable) / stats.expectedRows) * 100
}

export function completenessLabel(stats: MemoryStats): string {
  const value = completeness(stats)
  // Unknown must not render as a flattering number.
  return value === null ? tt('nemerané') : formatPercent(value, 1)
}

export function languageLabel(code: string): string {
  const entry = LANGUAGE_CATALOG.find((language) => language.code === code)
  return entry?.label ?? code
}

export function languageFlag(code: string): string {
  const entry = LANGUAGE_CATALOG.find((language) => language.code === code)
  return entry?.flag ?? ''
}

/** Every language the memory could hold, so a filter is not limited to the three the stats count. */
export function filterLanguages(): { code: string; label: string; flag: string }[] {
  return LANGUAGE_CATALOG.filter((language) => language.code !== 'sk').map((language) => ({
    code: language.code,
    label: language.label,
    flag: language.flag,
  }))
}

// ─── FB-160: the namespace filter ────────────────────────────────────────────

/**
 * The namespaces a page's rows happen to carry.
 *
 * Kept, because it is all the API offers — there is no `GET /translations/namespaces` — but reported as what
 * it is. Legacy presents the same list as *the* namespaces, and it changes as you page.
 */
export function namespacesOf(entries: readonly TranslationEntry[]): string[] {
  const found = new Set<string>()
  for (const entry of entries) {
    if (entry.namespace !== null) found.add(entry.namespace)
  }
  return [...found].sort((left, right) => left.localeCompare(right, 'sk'))
}

export function namespaceNote(entries: readonly TranslationEntry[], total: number): string {
  if (entries.length >= total) return ''
  return `${tt('Ponuka menných priestorov je len z tejto stránky')} (${formatCount(entries.length)} ${tt('z')} ${formatCount(total)}) - ${tt('iný sa tu nedá vybrať.')}`
}

// ─── The rows ────────────────────────────────────────────────────────────────

export type EntryState = 'manual' | 'untranslatable' | 'translated' | 'missing'

export function entryState(entry: TranslationEntry): EntryState {
  if (entry.isUntranslatable) return 'untranslatable'
  if (entry.isManualOverride) return 'manual'
  return entry.translation.trim() === '' ? 'missing' : 'translated'
}

export function entryStateLabel(state: EntryState): string {
  if (state === 'manual') return tt('Ručne')
  if (state === 'untranslatable') return tt('Neprekladá sa')
  if (state === 'missing') return tt('Chýba')
  return tt('Preložené')
}

// `groupBySource` was here and is deleted — §9.35, 2026-08-06. It grouped a language-agnostic listing
// by source text, as legacy does when no language is chosen.
//
// **The list is paged by the server**, and that settles it: `page` and `pageSize` are real, so
// grouping the fetched page would split a source's languages across the boundary — *Objednávka* with
// three translations at the bottom of one page and two at the top of the next, reading as two
// different sources. The same defect as sorting a page, which §9.35 has already found once.
//
// **And the row does not need it.** Each one prints `languageFlag(targetLang)` beside the
// translation, so a repeated source text is self-describing where legacy's grouped rows were not.


/** Only the three fields the PUT accepts, so an edit cannot send back a timestamp or a namespace. */
export function editPayload(entry: {
  translation: string
  isManualOverride: boolean
  isUntranslatable: boolean
}): Record<string, unknown> {
  return {
    translation: entry.translation,
    isManualOverride: entry.isManualOverride,
    isUntranslatable: entry.isUntranslatable,
  }
}

export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 0
  return Math.ceil(total / pageSize)
}

export function pageLabel(page: number, total: number, pageSize: number): string {
  const pages = pageCount(total, pageSize)
  if (pages === 0) return tt('Žiadne záznamy')
  return `${formatCount(page + 1)} / ${formatCount(pages)}`
}
